import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { UserService } from '../../services/user/user.service';
import { VisualNotificationService } from '../../services/visual-notification.service';

// Interfaces para el simulador de caja
interface Store {
  _id: { $oid: string };
  code: string;
  name: string;
  description: string;
  address: string;
  short_name: string;
  status: boolean;
  afiliation_number: string;
}

interface CashierSession {
  shop: string;
  cashRegister: string;
  cashierDocument: string;
  token?: string;
  storeName?: string;
}

interface ClientData {
  identity: string;
  transactionNumber?: number;
  fullContactInfo?: ContactInfo;
  personCompleteData?: PersonCompleteData; // ← Información completa obtenida con getPersonbyCode
}

interface ContactInfo {
  person: {
    code: string;
    name: string;              // ← Nombre real del campo
    lastName: string;          // ← Apellido real del campo
    second_name?: string;      // ← Segundo nombre real
    second_lastName?: string;  // ← Segundo apellido real
    identity?: string;         // ← Cédula del nivel superior
    telefono?: string;
    email?: string;
    file_selfie?: {            // ← Estructura correcta del selfie
      url: string;
    };
    gender?: string;           // ← Para avatares por defecto
  };
  identity?: string;           // ← Cédula del nivel superior
  code_identity?: string;      // ← Tipo de identificación
}

interface PersonCompleteData {
  code: string;
  name: string;
  lastName: string;
  second_name?: string;
  second_lastName?: string;
  identity?: string;
  telefono?: string;
  email?: string;
  file_selfie?: {
    url: string;
    type_file?: string;
    base_name?: string;
  };
  gender?: string;
}

interface Installment {
  codigo: string;
  fecha_vencimiento: string;
  monto_usd: number;
  monto_deuda_usd: number;
  num_cuota: number;
  cuota_vencida: boolean;
}

interface Invoice {
  _id: string;
  identificador: string;
  numero_credito: number;
  codigo: string;
  plan: string;
  proveedor: string;
  moneda: string;
  total_compra_usd: number;
  monto_credito: number;
  deuda_credito: number;
  cuotas: Installment[];
  principal?: boolean;
}

interface Person {
  codigo: string;
  nombre: string;
  apellido: string;
  segundo_nombre: string;
  segundo_apellido: string;
  tipo_identificacion: string;
  codigo_identificacion: string;
  numero_identificacion: string;
  montoDeudaTotalUSD: number;
  invoices: Invoice[];
}

interface DebtResponse {
  res: {
    person: Person;
    transactionNumber: number;
    identity: string;
  };
}

interface ClientTransaction {
  _id: string;
  status: 'IN_PROCESS' | 'IN_PAYMENT' | 'FINISHED' | 'CANCELLED';
  date: string;
  code: string;
  amount?: number;
  customer_name?: string;
  customer_phone?: string;
  payment_method?: string;
  reference?: string;
  store_name?: string;
  store_code?: string;
  cashier_document?: string;
  created_at?: string;
  updated_at?: string;
  description?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
  type: 'cash' | 'card' | 'digital' | 'bank_transfer';
  enabled: boolean;
  icon: string;
  commission: number;
}

interface Transaction {
  id: string;
  amount: number;
  paymentMethod: PaymentMethod;
  timestamp: Date;
  status: 'pending' | 'completed' | 'failed';
  reference: string;
  customerName?: string;
  customerPhone?: string;
}

@Component({
  selector: 'app-cash-register-simulator',
  templateUrl: './cash-register-simulator.component.html',
  styleUrls: ['./cash-register-simulator.component.css']
})
export class CashRegisterSimulatorComponent implements OnInit, OnDestroy {

  // Estados del simulador
  isActive: boolean = false;
  currentTransaction: Transaction | null = null;
  transactionHistory: Transaction[] = [];
  
  // Nuevos estados para el flujo de caja
  currentView: 'login' | 'client-search' | 'options' | 'transactions' | 'debts' = 'login';
  cashierSession: CashierSession | null = null;
  clientData: ClientData | null = null;
  stores: Store[] = [];
  filteredStores: Store[] = [];
  showLoginModal: boolean = false;
  isLoadingStores: boolean = false;
  isAuthenticating: boolean = false;
  
  // Datos de consultas
  currentDebts: Person | null = null;
  currentTransactions: ClientTransaction[] = [];
  currentTransactionNumber: number | null = null;
  
  // === MEMORY/CACHE SYSTEM ===
  hasActiveQuery: boolean = false;
  cachedQueryData: any = null;
  cachedClientIdentity: string | null = null;
  
  // Estado de colapso de cuotas por invoice (legacy - se mantendrá para backward compatibility)
  collapsedInstallments: { [key: string]: boolean } = {};
  
  // === SLIDING DRAWER STATE ===
  isDrawerOpen: boolean = false;
  selectedInvoice: Invoice | null = null;
  selectedInvoiceIndex: number = -1;
  
  // === PAYMENT REGISTRATION STATE ===
  isProcessingPayment: boolean = false;
  showPaymentModal: boolean = false;
  paymentRegistrationForm: FormGroup;
  
  // === COMPLETE PAYMENT STATE ===
  isCompletePaymentMode: boolean = false;
  selectedTransactionForCompletion: any = null;
  
  // === TRANSACTIONS STATE ===
  inProcessTransactions: any[] = [];
  inPaymentTransactions: any[] = [];
  finishedTransactions: any[] = [];
  isLoadingTransactions: boolean = false;
  isTransactionOrderAsc: boolean = false; // false = más reciente primero (default)
  
  // === MULTIPLE SELECTION SYSTEM ===
  selectedTransactions: Set<number> = new Set();
  isSelectionMode: boolean = false;
  
  // === PAYMENT DETAIL MODAL ===
  isPaymentDetailModalOpen: boolean = false;
  selectedPaymentDetail: any = null;
  isLoadingPaymentDetail: boolean = false;
  banks: any = {};
  
  // Métodos de pago disponibles para caja registradora
  cashRegisterPaymentMethods = [
    { name: 'Mercantil - Crédito', code: 'BM080-B' },
    { name: 'Mercantil - AmericanExpress', code: 'BM080-A' },
    { name: 'Mercantil - Débito', code: 'BM080-D' },
    { name: 'Mercantil - P2C', code: 'BM080-M' }
  ];
  
  // Formularios
  paymentForm: FormGroup;
  configForm: FormGroup;
  loginForm: FormGroup;
  clientForm: FormGroup;
  
  // Opciones de pago disponibles
  paymentMethods: PaymentMethod[] = [
    {
      id: 'cash',
      name: 'Efectivo',
      type: 'cash',
      enabled: true,
      icon: 'fas fa-money-bill-wave',
      commission: 0
    },
    {
      id: 'visa',
      name: 'Tarjeta Visa',
      type: 'card',
      enabled: true,
      icon: 'fab fa-cc-visa',
      commission: 2.5
    },
    {
      id: 'mastercard',
      name: 'Tarjeta Mastercard',
      type: 'card',
      enabled: true,
      icon: 'fab fa-cc-mastercard',
      commission: 2.5
    },
    {
      id: 'payall',
      name: 'PayAll',
      type: 'digital',
      enabled: true,
      icon: 'fas fa-mobile-alt',
      commission: 1.5
    },
    {
      id: 'bank_transfer',
      name: 'Transferencia Bancaria',
      type: 'bank_transfer',
      enabled: true,
      icon: 'fas fa-university',
      commission: 0.5
    }
  ];

  // Configuración del simulador
  simulatorConfig = {
    cashRegisterUrl: environment.BASE_CASH_REGISTER_PAYMENT,  // URL desde environment.ts
    isConnected: false,
    lastPing: null as Date | null
  };

  // Stats del día
  dailyStats = {
    totalTransactions: 0,
    totalAmount: 0,
    successfulTransactions: 0,
    failedTransactions: 0,
    totalCommissions: 0
  };

  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private notification: VisualNotificationService,
    private ngZone: NgZone
  ) {
    // Inicializar formularios
    this.paymentForm = this.formBuilder.group({
      amount: ['', [Validators.required, Validators.min(1), Validators.max(10000000)]],
      paymentMethod: ['', Validators.required],
      customerName: [''],
      customerPhone: ['', [Validators.pattern(/^[0-9]{10}$/)]]
    });

    this.configForm = this.formBuilder.group({
      cashRegisterUrl: [this.simulatorConfig.cashRegisterUrl, Validators.required],
      autoRetry: [true],
      retryAttempts: [3, [Validators.min(1), Validators.max(10)]],
      timeout: [30000, [Validators.min(5000), Validators.max(120000)]]
    });

    // Nuevos formularios para el flujo de caja
    this.loginForm = this.formBuilder.group({
      selectedStore: ['', Validators.required],
      storeSearch: [''],
      cashRegister: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      cashierDocument: ['', [Validators.required, Validators.pattern(/^[0-9]{7,10}$/)]]
    });

    this.clientForm = this.formBuilder.group({
      identity: ['', [Validators.required, Validators.pattern(/^[0-9]{7,10}$/)]]
    });
    
    this.paymentRegistrationForm = this.formBuilder.group({
      amount: ['', [Validators.required, Validators.min(0.01)]],
      paymentMethod: ['', Validators.required],
      reference: ['', Validators.required],
      description: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadTransactionHistory();
    this.calculateDailyStats();
    this.checkConnection();
    this.loadStores();
    this.getBanks(); // Cargar bancos para el modal de detalle
    
    // Listener para el buscador de tiendas
    this.loginForm.get('storeSearch')?.valueChanges.subscribe(searchTerm => {
      this.filterStores(searchTerm);
    });
  }

  // === CONTROL DEL SIMULADOR ===

  toggleSimulator(): void {
    if (this.isActive) {
      this.stopSimulator();
    } else {
      // Abrir modal de login en lugar de iniciar directamente
      this.showLoginModal = true;
    }
  }

  private startSimulator(): void {
    this.isActive = true;
    console.log('Simulador de caja registradora iniciado');
    this.notification.crediAlert('Simulador de caja registradora iniciado', 'success');
    this.checkConnection();
  }

  private stopSimulator(): void {
    console.log('Deteniendo simulador y cerrando sesión...');
    // Llamar a logout para limpiar TODA la sesión de manera segura
    this.logout();
  }

  // === GESTIÓN DE PAGOS ===

  processPayment(): void {
    if (!this.paymentForm.valid) {
      this.notification.crediAlert('Por favor, complete todos los campos requeridos', 'warning');
      return;
    }

    const formData = this.paymentForm.value;
    const selectedMethod = this.paymentMethods.find(m => m.id === formData.paymentMethod);

    if (!selectedMethod) {
      this.notification.crediAlert('Método de pago no válido', 'error');
      return;
    }

    // Crear nueva transacción
    const transaction: Transaction = {
      id: this.generateTransactionId(),
      amount: formData.amount,
      paymentMethod: selectedMethod,
      timestamp: new Date(),
      status: 'pending',
      reference: this.generateReference(),
      customerName: formData.customerName || undefined,
      customerPhone: formData.customerPhone || undefined
    };

    this.currentTransaction = transaction;
    console.log(`Procesando pago de $${formData.amount.toLocaleString()} via ${selectedMethod.name}`);

    // Simular procesamiento
    this.simulatePaymentProcessing(transaction);
  }

  private simulatePaymentProcessing(transaction: Transaction): void {
    // Simular delay de procesamiento
    setTimeout(() => {
      // 90% de probabilidad de éxito
      const isSuccess = Math.random() > 0.1;
      
      transaction.status = isSuccess ? 'completed' : 'failed';
      
      if (isSuccess) {
        console.log(`Pago completado exitosamente - Ref: ${transaction.reference}`);
        this.notification.crediAlert(`Pago completado exitosamente - Ref: ${transaction.reference}`, 'success');
        this.dailyStats.successfulTransactions++;
        this.dailyStats.totalAmount += transaction.amount;
        this.dailyStats.totalCommissions += this.calculateCommission(transaction);
      } else {
        console.log(`Error al procesar el pago - Ref: ${transaction.reference}`);
        this.notification.crediAlert(`Error al procesar el pago - Ref: ${transaction.reference}`, 'error');
        this.dailyStats.failedTransactions++;
      }

      this.dailyStats.totalTransactions++;
      this.transactionHistory.unshift(transaction);
      this.saveTransactionHistory();
      this.currentTransaction = null;
      this.paymentForm.reset();

    }, Math.random() * 3000 + 1000); // Entre 1-4 segundos
  }

  // === PAYMENT REGISTRATION MANAGEMENT ===
  
  async initializePayment(): Promise<void> {
    if (!this.currentDebts?.invoices || !this.currentTransactionNumber) {
      this.notification.crediAlert('No hay información suficiente para procesar el pago', 'error');
      return;
    }

    if (!this.cashierSession?.token) {
      this.notification.crediAlert('Sesión de cajero no válida. Por favor, inicia sesión nuevamente.', 'error');
      return;
    }

    // ⚠️ CONFIRMACIÓN: Cambiar estado a "EN PAGO"
    const confirmed = await this.notification.crediConfirm(`¿Está seguro que desea cambiar el estado a "EN PAGO" de la transacción número ${this.currentTransactionNumber}?`);
    if (!confirmed) {
      console.log('💭 Usuario canceló el cambio de estado a EN PAGO');
      return;
    }

    // Obtener primer invoice y primera cuota (como fallback para el endpoint)
    const firstInvoice = this.currentDebts.invoices[0];
    const firstInstallment = firstInvoice.cuotas?.[0];
    
    if (!firstInvoice || !firstInstallment) {
      this.notification.crediAlert('No se encontraron datos válidos para el pago', 'error');
      return;
    }

    this.isProcessingPayment = true;

    try {
      const headers = {
        'Authorization': this.cashierSession?.token,  // Sin "Bearer" - API lo espera directo
        'Content-Type': 'application/json',
        'X-Skip-Auth-Interceptor': 'true'  // Header para evitar logout automático
      };

      const url = `${this.simulatorConfig.cashRegisterUrl}/api/payments/in_process_payment`;
      const params = {
        transactionNumber: this.currentTransactionNumber,
        invoice: firstInvoice.numero_credito,
        installment: firstInstallment.num_cuota
      };

      const response = await this.http.get<any>(url, { 
        headers, 
        params 
      }).toPromise();

      console.log('=== RESPUESTA INICIALIZACIÓN PAGO ===');
      console.log(response);

      if (response?.res?.status === 'IN_PAYMENT') {
        // Pago inicializado correctamente, mostrar modal para capturar datos
        this.showPaymentModal = true;
        this.paymentRegistrationForm.reset();
        
        // Prevenir scroll del body
        document.body.style.overflow = 'hidden';
      } else {
        this.notification.crediAlert('No se pudo inicializar el pago. Intenta nuevamente.', 'error');
      }

    } catch (error) {
      console.error('Error inicializando pago:', error);
      this.notification.crediAlert('Error al comunicarse con el servidor. Verifica tu conexión.', 'error');
    } finally {
      this.isProcessingPayment = false;
    }
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
    this.paymentRegistrationForm.reset();
    
    // Limpiar estado de completar pago
    this.isCompletePaymentMode = false;
    this.selectedTransactionForCompletion = null;
    
    // Restaurar scroll del body
    document.body.style.overflow = 'auto';
  }

  /**
   * Abre el modal de pago para completar una transacción IN_PAYMENT
   */
  openCompletePaymentModal(transaction: any): void {
    console.log('🎯 Abriendo modal para completar pago de transacción:', transaction);
    
    // Configurar modo de completar pago
    this.isCompletePaymentMode = true;
    this.selectedTransactionForCompletion = transaction;
    
    // Pre-llenar el formulario con información de la transacción
    this.paymentRegistrationForm.reset();
    this.paymentRegistrationForm.patchValue({
      description: `Completando pago - Transacción #${transaction.transactionNumber} - ${transaction.person?.nombre} ${transaction.person?.apellido}`
    });
    
    // Abrir el modal
    this.showPaymentModal = true;
    
    // Prevenir scroll del body
    document.body.style.overflow = 'hidden';
    
    console.log('✅ Modal de completar pago configurado correctamente');
  }

  async submitPayment(): Promise<void> {
    if (!this.paymentRegistrationForm.valid) {
      this.notification.crediAlert('Por favor, completa todos los campos requeridos', 'warning');
      return;
    }

    // Validar que tengamos los datos necesarios según el modo
    if (this.isCompletePaymentMode) {
      // Modo completar pago: validar transacción seleccionada
      if (!this.selectedTransactionForCompletion?.identity || !this.selectedTransactionForCompletion?.transactionNumber) {
        this.notification.crediAlert('No hay información suficiente de la transacción para completar el pago', 'error');
        return;
      }
    } else {
      // Modo normal: validar datos del cliente actual
      if (!this.clientData?.identity || !this.currentTransactionNumber) {
        this.notification.crediAlert('No hay información suficiente para registrar el pago', 'error');
        return;
      }
    }

    if (!this.cashierSession?.token) {
      this.notification.crediAlert('Sesión de cajero no válida. Por favor, inicia sesión nuevamente.', 'error');
      return;
    }

    this.isProcessingPayment = true;

    try {
      const formData = this.paymentRegistrationForm.value;
      
      // Encontrar el nombre del método de pago para mostrar
      const selectedMethod = this.cashRegisterPaymentMethods.find(m => m.code === formData.paymentMethod);
      
      // Mostrar confirmación con todos los datos antes de enviar
      const confirmMessage = `
        <div style="text-align: left; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
          <p style="margin-bottom: 15px; font-size: 16px; color: #374151;">
            <strong>¿Confirmar el registro del pago con los siguientes datos?</strong>
          </p>
          
          <div style="background: #f8fafc; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin: 15px 0;">
            <h4 style="margin: 0 0 12px 0; color: #1f2937; font-size: 14px; font-weight: 600;">
              📋 DATOS DEL PAGO
            </h4>
            <ul style="margin: 0; padding-left: 20px; list-style: none; line-height: 1.6;">
              <li style="margin-bottom: 8px;"><strong>Cliente:</strong> ${this.clientData?.identity || 'N/A'}</li>
              <li style="margin-bottom: 8px;"><strong>Transacción:</strong> #${this.currentTransactionNumber}</li>
              <li style="margin-bottom: 8px;"><strong>Monto:</strong> <span style="color: #059669; font-weight: 600;">Bs ${formData.amount}</span></li>
              <li style="margin-bottom: 8px;"><strong>Método:</strong> ${selectedMethod?.name || formData.paymentMethod}${selectedMethod?.code ? ` (${selectedMethod.code})` : ''}</li>
              <li style="margin-bottom: 8px;"><strong>Referencia:</strong> ${formData.reference || 'Sin referencia'}</li>
              <li style="margin-bottom: 8px;"><strong>Descripción:</strong> ${formData.description || 'Pago desde caja registradora'}</li>
            </ul>
          </div>
          
          <p style="margin-top: 15px; font-size: 15px; color: #dc2626; font-weight: 500;">
            ¿Está seguro de procesar este pago?
          </p>
        </div>
      `;

      const confirmed = await this.notification.crediConfirm(confirmMessage);
      if (!confirmed) {
        console.log('💭 Usuario canceló el registro del pago');
        this.isProcessingPayment = false;
        return;
      }
      
      // Preparar datos para el endpoint /api/payments/register_payment
      const paymentData = {
        reference: formData.reference || '',
        description: formData.description || 'Pago desde caja registradora',
        identity: this.isCompletePaymentMode 
          ? this.selectedTransactionForCompletion.identity 
          : this.clientData?.identity || '',
        transactionNumber: this.isCompletePaymentMode 
          ? this.selectedTransactionForCompletion.transactionNumber 
          : this.currentTransactionNumber,
        amount: parseFloat(formData.amount),
        sapCode: formData.paymentMethod, // El código SAP del método de pago seleccionado
        success: true // Siempre true según especificación
      };

      console.log('💰 Registrando pago con datos confirmados:', paymentData);

      // Headers con JWT del login (misma configuración que otros endpoints)
      const headers = {
        'Authorization': this.cashierSession?.token,  // Sin "Bearer" - API lo espera directo
        'Content-Type': 'application/json',
        'X-Skip-Auth-Interceptor': 'true'  // Header para evitar logout automático
      };

      // Llamada real al API de registro de pago
      const url = `${this.simulatorConfig.cashRegisterUrl}/api/payments/register_payment`;
      
      const response = await this.http.post<any>(url, paymentData, { headers }).toPromise();
      
      console.log('✅ Pago registrado exitosamente:', response);
      
      // Manejar el flujo según el modo
      if (this.isCompletePaymentMode) {
        // Modo completar pago: mantener vista de transacciones y refrescar
        this.notification.crediAlert('¡Pago completado exitosamente!<br><br>La transacción ha sido finalizada correctamente.', 'success');
        
        // Cerrar modal de pago
        this.closePaymentModal();
        
        // Recargar transacciones para reflejar el cambio (IN_PAYMENT → FINISHED)
        await this.loadAllTransactions();
        
        console.log('🔄 Transacciones actualizadas después de completar pago');
      } else {
        // Modo normal: flujo original
        this.notification.crediAlert('¡Pago registrado exitosamente!<br><br>La transacción ha sido completada y actualizada a estado FINALIZADA.', 'success');
        
        // Cerrar modal de pago
        this.closePaymentModal();
        
        // Limpiar completamente la memoria de consulta anterior
        this.clearQueryMemory();
        
        // Volver a la vista principal (opciones de consultar deudas o ver transacciones)
        this.currentView = 'options';
      }

    } catch (error) {
      console.error('❌ Error al registrar pago:', error);
      this.notification.crediAlert('Error al registrar el pago. Inténtalo nuevamente.', 'error');
    } finally {
      this.isProcessingPayment = false;
    }
  }

  // === SLIDING DRAWER MANAGEMENT ===
  
  openDrawer(invoice: Invoice): void {
    if (!this.currentDebts?.invoices) return;
    
    this.selectedInvoice = invoice;
    this.selectedInvoiceIndex = this.currentDebts.invoices.findIndex(inv => inv._id === invoice._id);
    this.isDrawerOpen = true;
    
    // Prevenir scroll del body cuando el drawer está abierto
    document.body.style.overflow = 'hidden';
  }
  
  closeDrawer(): void {
    this.isDrawerOpen = false;
    this.selectedInvoice = null;
    this.selectedInvoiceIndex = -1;
    
    // Restaurar scroll del body
    document.body.style.overflow = 'auto';
  }
  
  navigateInvoice(direction: 'prev' | 'next'): void {
    if (!this.currentDebts?.invoices || this.selectedInvoiceIndex === -1) return;
    
    const invoices = this.currentDebts.invoices;
    let newIndex = this.selectedInvoiceIndex;
    
    if (direction === 'prev' && newIndex > 0) {
      newIndex--;
    } else if (direction === 'next' && newIndex < invoices.length - 1) {
      newIndex++;
    }
    
    if (newIndex !== this.selectedInvoiceIndex) {
      this.selectedInvoiceIndex = newIndex;
      this.selectedInvoice = invoices[newIndex];
    }
  }
  
  // === QUERY MEMORY MANAGEMENT ===
  
  async refreshQuery(): Promise<void> {
    if (!this.currentTransactionNumber) {
      console.log('No hay transacción activa para cancelar');
      this.forceNewQuery();
      return;
    }
    
    console.log('🔄 Refrescando consulta - cancelando transacción actual:', this.currentTransactionNumber);
    
    try {
      // Cancelar la transacción actual si existe - verificar si fue exitosa
      const wasCancelled = await this.cancelTransaction(this.currentTransactionNumber);
      
      if (wasCancelled) {
        // Solo si la cancelación fue exitosa, limpiar memoria y hacer nueva consulta
        this.clearQueryMemory();
        this.consultDebts();
      } else {
        console.log('💭 Usuario canceló la operación de refrescar consulta');
        // No hacer nada, mantener el estado actual
      }
      
    } catch (error) {
      console.error('Error al refrescar consulta:', error);
      // En caso de error, forzar nueva consulta sin cancelar
      this.forceNewQuery();
    }
  }
  
  forceNewQuery(): void {
    console.log('🚀 Forzando nueva consulta');
    this.clearQueryMemory();
    this.consultDebts();
  }
  
  clearQueryMemory(): void {
    console.log('🧹 Limpiando memoria de consulta');
    this.hasActiveQuery = false;
    this.cachedQueryData = null;
    this.cachedClientIdentity = null;
    this.currentDebts = null;
    this.currentTransactionNumber = null;
  }

  // === TRANSACTIONS MANAGEMENT ===
  
  async cancelTransaction(transactionNumber: number, skipConfirmation: boolean = false): Promise<boolean> {
    if (!this.cashierSession?.token) {
      this.notification.crediAlert('Sesión de cajero no válida. Por favor, inicia sesión nuevamente.', 'error');
      return false;
    }

    // Confirmar la cancelación (solo si no es selección múltiple)
    if (!skipConfirmation) {
      const confirmed = await this.notification.crediConfirm(`¿Estás seguro de que quieres cancelar la transacción #${transactionNumber}?`);
      if (!confirmed) {
        return false; // Usuario canceló la operación
      }
    }

    try {
      const headers = {
        'Authorization': this.cashierSession?.token,
        'Content-Type': 'application/json',
        'X-Skip-Auth-Interceptor': 'true'
      };

      const url = `${this.simulatorConfig.cashRegisterUrl}/api/payments/in_process_payment_rollback?numTransaccion=${transactionNumber}`;
      
      console.log('🔄 Cancelando transacción:', transactionNumber);
      const response = await this.http.get(url, { headers }).toPromise();
      
      console.log('✅ Transacción cancelada exitosamente:', response);
      
      // Si la transacción cancelada es la que está en memoria, limpiar la memoria
      if (this.currentTransactionNumber === transactionNumber) {
        console.log('🧠 La transacción cancelada era la consulta activa - limpiando memoria');
        this.clearQueryMemory();
      }
      
      // Refrescar las listas y mostrar alerta solo si no es selección múltiple
      if (!skipConfirmation) {
        await this.loadAllTransactions();
        this.notification.crediAlert(`Transacción #${transactionNumber} cancelada exitosamente.`, 'success');
      }
      
      return true; // Cancelación exitosa
      
    } catch (error) {
      console.error('❌ Error al cancelar transacción:', error);
      if (!skipConfirmation) {
        this.notification.crediAlert('Error al cancelar la transacción. Por favor, intenta nuevamente.', 'error');
      }
      throw error; // Re-throw para que deleteSelectedTransactions pueda manejarlo
    }
    
    return false; // En caso de error (aunque no debería llegar aquí por el throw)
  }
  
  async loadAllTransactions(): Promise<void> {
    if (!this.clientData?.identity) {
      console.log('No hay cliente seleccionado para cargar transacciones');
      return;
    }

    this.isLoadingTransactions = true;
    
    try {
      // Cargar las 3 listas en paralelo
      await Promise.all([
        this.loadTransactionsByStatus('IN_PROCESS'),
        this.loadTransactionsByStatus('IN_PAYMENT'),
        this.loadTransactionsByStatus('FINISHED')
      ]);
      
      console.log('=== TODAS LAS TRANSACCIONES CARGADAS ===');
      console.log('IN_PROCESS:', this.inProcessTransactions);
      console.log('IN_PAYMENT:', this.inPaymentTransactions);
      console.log('FINISHED:', this.finishedTransactions);
      
    } catch (error) {
      console.error('Error cargando transacciones:', error);
    } finally {
      this.isLoadingTransactions = false;
    }
  }

  private async loadTransactionsByStatus(status: 'IN_PROCESS' | 'IN_PAYMENT' | 'FINISHED'): Promise<void> {
    if (!this.clientData?.identity || !this.cashierSession?.token) {
      return;
    }

    try {
      const headers = {
        'Authorization': this.cashierSession.token,
        'Content-Type': 'application/json',
        'X-Skip-Auth-Interceptor': 'true'
      };

      const url = `${this.simulatorConfig.cashRegisterUrl}/api/payments/client_transactions`;
      const params = {
        identity: this.clientData.identity,
        status: status
      };

      const response = await this.http.get<any>(url, { 
        headers, 
        params 
      }).toPromise();

      console.log(`=== RESPUESTA TRANSACCIONES ${status} ===`);
      console.log(response);

      // Almacenar en la propiedad correspondiente y ordenar
      let transactions = response?.res || [];
      transactions = this.sortTransactions(transactions);
      
      switch (status) {
        case 'IN_PROCESS':
          this.inProcessTransactions = transactions;
          break;
        case 'IN_PAYMENT':
          this.inPaymentTransactions = transactions;
          break;
        case 'FINISHED':
          this.finishedTransactions = transactions;
          break;
      }

    } catch (error) {
      console.error(`Error cargando transacciones ${status}:`, error);
      
      // Inicializar arrays vacíos en caso de error
      switch (status) {
        case 'IN_PROCESS':
          this.inProcessTransactions = [];
          break;
        case 'IN_PAYMENT':
          this.inPaymentTransactions = [];
          break;
        case 'FINISHED':
          this.finishedTransactions = [];
          break;
      }
    }
  }

  // === TRANSACTION SORTING ===
  
  sortTransactions(transactions: any[]): any[] {
    console.log('🔄 Volteando lista:', transactions?.length || 0, 'transacciones');
    
    if (!transactions || transactions.length === 0) {
      console.log('❌ Lista vacía');
      return transactions || [];
    }
    
    // SIMPLE: Solo voltear la lista
    const reversed = [...transactions].reverse();
    
    console.log('✅ Lista volteada:', reversed?.length || 0, 'transacciones');
    console.log(`   Primera antes: #${transactions[0]?.transactionNumber} → Primera después: #${reversed[0]?.transactionNumber}`);
    
    return reversed;
  }

  toggleTransactionOrder(): void {
    // Cambiar el orden
    this.isTransactionOrderAsc = !this.isTransactionOrderAsc;
    
    console.log(`=== INVIRTIENDO ORDEN DE TRANSACCIONES ===`);
    console.log(`Acción: Voltear todas las listas`);
    console.log('Estado ANTES de voltear:');
    console.log('IN_PROCESS:', this.inProcessTransactions?.length || 0, 'transacciones', this.inProcessTransactions);
    console.log('IN_PAYMENT:', this.inPaymentTransactions?.length || 0, 'transacciones', this.inPaymentTransactions);
    console.log('FINISHED:', this.finishedTransactions?.length || 0, 'transacciones', this.finishedTransactions);
    
    // VOLTEAR todas las listas
    console.log('🟡 Volteando IN_PROCESS...');
    this.inProcessTransactions = [...this.sortTransactions(this.inProcessTransactions)];
    
    console.log('🔵 Volteando IN_PAYMENT...');
    this.inPaymentTransactions = [...this.sortTransactions(this.inPaymentTransactions)];
    
    console.log('🟢 Volteando FINISHED...');
    this.finishedTransactions = [...this.sortTransactions(this.finishedTransactions)];
    
    console.log('=== VOLTEO COMPLETADO ===');
    
    // FORZAR detección de cambios en Angular
    console.log('🔄 Forzando detección de cambios...');
    this.cdr.detectChanges();
    console.log('✅ Detección de cambios forzada completada');
  }

  // === TRACKING FUNCTIONS ===
  
  trackByTransactionNumber(index: number, transaction: any): any {
    return transaction.transactionNumber || index;
  }

  // === MULTIPLE SELECTION MANAGEMENT ===
  
  toggleSelectionMode(): void {
    this.isSelectionMode = !this.isSelectionMode;
    if (!this.isSelectionMode) {
      this.selectedTransactions.clear();
    }
    console.log('Modo selección:', this.isSelectionMode ? 'ACTIVADO' : 'DESACTIVADO');
  }
  
  toggleTransactionSelection(transactionNumber: number): void {
    if (this.selectedTransactions.has(transactionNumber)) {
      this.selectedTransactions.delete(transactionNumber);
    } else {
      this.selectedTransactions.add(transactionNumber);
    }
    console.log('Transacciones seleccionadas:', Array.from(this.selectedTransactions));
  }
  
  isTransactionSelected(transactionNumber: number): boolean {
    return this.selectedTransactions.has(transactionNumber);
  }
  
  selectAllVisibleTransactions(): void {
    // Seleccionar todas las transacciones cancelables (IN_PROCESS e IN_PAYMENT)
    this.inProcessTransactions.forEach(t => this.selectedTransactions.add(t.transactionNumber));
    this.inPaymentTransactions.forEach(t => this.selectedTransactions.add(t.transactionNumber));
    console.log('Todas las transacciones cancelables seleccionadas:', Array.from(this.selectedTransactions));
  }
  
  clearAllSelections(): void {
    this.selectedTransactions.clear();
    console.log('Selección limpiada');
  }
  
  async deleteSelectedTransactions(): Promise<void> {
    if (this.selectedTransactions.size === 0) {
      this.notification.crediAlert('No hay transacciones seleccionadas', 'warning');
      return;
    }
    
    const count = this.selectedTransactions.size;
    const confirmed = await this.notification.crediConfirm(`¿Está seguro que desea borrar ${count} transacción${count > 1 ? 'es' : ''}?`);
    
    if (!confirmed) return;
    
    console.log('🗑️ Iniciando cancelación masiva de transacciones:', Array.from(this.selectedTransactions));
    
    const transactionNumbers = Array.from(this.selectedTransactions);
    let successCount = 0;
    let errorCount = 0;
    const failedTransactions: number[] = [];
    
    // Cancelar todas las transacciones seleccionadas SIN alertas individuales
    for (const transactionNumber of transactionNumbers) {
      try {
        await this.cancelTransaction(transactionNumber, true); // skipConfirmation = true
        successCount++;
        console.log(`✅ Transacción ${transactionNumber} cancelada exitosamente`);
      } catch (error) {
        console.error(`❌ Error cancelando transacción ${transactionNumber}:`, error);
        errorCount++;
        failedTransactions.push(transactionNumber);
      }
    }
    
    // Limpiar selección y salir del modo selección
    this.selectedTransactions.clear();
    this.isSelectionMode = false;
    
    // Refrescar las listas UNA SOLA VEZ al final
    await this.loadAllTransactions();
    
    // Mostrar UN SOLO resultado final
    if (errorCount === 0) {
      this.notification.crediAlert(`✅ Se cancelaron exitosamente ${successCount} transacción${successCount > 1 ? 'es' : ''}.`, 'success');
    } else {
      const failedList = failedTransactions.join(', ');
      this.notification.crediAlert(`⚠️ Resultado: ${successCount} canceladas exitosamente, ${errorCount} fallaron.<br><br>Transacciones fallidas: ${failedList}`, 'warning');
    }
    
    console.log('🏁 Cancelación masiva completada:', { successCount, errorCount, failedTransactions });
  }

  // === GESTIÓN DE COLAPSO DE CUOTAS (Legacy) ===
  
  toggleInstallments(invoiceId: string): void {
    this.collapsedInstallments[invoiceId] = !this.collapsedInstallments[invoiceId];
  }
  
  isInstallmentsCollapsed(invoiceId: string): boolean {
    // Por defecto colapsado (true), a menos que esté explícitamente expandido (false)
    return this.collapsedInstallments[invoiceId] !== false;
  }

  // === UTILIDADES ===
  
  getOverdueInstallmentsCount(invoice: Invoice): number {
    if (!invoice.cuotas) return 0;
    return invoice.cuotas.filter(cuota => cuota.cuota_vencida).length;
  }

  private generateTransactionId(): string {
    return 'TXN_' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substr(2, 5).toUpperCase();
  }

  private generateReference(): string {
    return 'REF_' + Date.now().toString(36).toUpperCase();
  }

  private calculateCommission(transaction: Transaction): number {
    return (transaction.amount * transaction.paymentMethod.commission) / 100;
  }

  // === GESTIÓN DE DATOS ===

  private loadTransactionHistory(): void {
    const saved = localStorage.getItem('cash_register_transactions');
    if (saved) {
      try {
        this.transactionHistory = JSON.parse(saved).map((t: any) => ({
          ...t,
          timestamp: new Date(t.timestamp)
        }));
      } catch (error) {
        console.error('Error loading transaction history:', error);
        this.transactionHistory = [];
      }
    }
  }

  private saveTransactionHistory(): void {
    localStorage.setItem('cash_register_transactions', JSON.stringify(this.transactionHistory));
  }

  private calculateDailyStats(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayTransactions = this.transactionHistory.filter(t => {
      const txDate = new Date(t.timestamp);
      txDate.setHours(0, 0, 0, 0);
      return txDate.getTime() === today.getTime();
    });

    this.dailyStats = {
      totalTransactions: todayTransactions.length,
      totalAmount: todayTransactions.filter(t => t.status === 'completed').reduce((sum, t) => sum + t.amount, 0),
      successfulTransactions: todayTransactions.filter(t => t.status === 'completed').length,
      failedTransactions: todayTransactions.filter(t => t.status === 'failed').length,
      totalCommissions: todayTransactions.filter(t => t.status === 'completed').reduce((sum, t) => sum + this.calculateCommission(t), 0)
    };
  }

  // === CONEXIÓN Y ESTADO ===

  private checkConnection(): void {
    // Simular check de conexión
    setTimeout(() => {
      this.simulatorConfig.isConnected = Math.random() > 0.2; // 80% probabilidad de conexión
      this.simulatorConfig.lastPing = new Date();
      
      if (!this.simulatorConfig.isConnected) {
        console.log('No se puede conectar con la API de caja registradora');
      }
    }, 1000);
  }

  // === ACCIONES DE UI ===

  async clearHistory(): Promise<void> {
    if (await this.notification.crediConfirm('¿Está seguro de que desea limpiar todo el historial de transacciones?')) {
      this.transactionHistory = [];
      this.saveTransactionHistory();
      this.calculateDailyStats();
      this.notification.crediAlert('Historial de transacciones limpiado', 'success');
    }
  }

  exportHistory(): void {
    const dataStr = JSON.stringify(this.transactionHistory, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `cash_register_history_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    this.notification.crediAlert('Historial exportado exitosamente', 'success');
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getPaymentMethodIcon(method: PaymentMethod): string {
    return method.icon;
  }

  refreshConnection(): void {
    this.simulatorConfig.isConnected = false;
    console.log('Verificando conexión...');
    this.checkConnection();
  }

  // === GESTIÓN DE TIENDAS Y LOGIN ===

  loadStores(): void {
    this.isLoadingStores = true;
    
    // Llamada real al API de tiendas
    const storeHeaders = {
      'X-Skip-Auth-Interceptor': 'true'
    };

    this.http.get<any>(`${environment.BASE_CASH_REGISTER_PAYMENT}/api/payments/shops`, { headers: storeHeaders })
      .subscribe({
        next: (response) => {
          // Procesar respuesta del API - la respuesta viene en response.res
          const allStores = response.res || response.shops || response || [];
          // Filtrar solo tiendas que tengan afiliation_number
          this.stores = allStores.filter((store: any) => store.afiliation_number);
          this.filteredStores = [...this.stores];
          this.isLoadingStores = false;
          console.log('Tiendas cargadas:', this.stores.length);
        },
        error: (error) => {
          console.error('Error cargando tiendas:', error);
          this.isLoadingStores = false;
          
          // Fallback: usar tiendas simuladas si falla la API
          this.stores = [
            {
              _id: { $oid: "64d5379038242fbb1ee14d52" },
              code: "1072",
              name: "SUCURSAL 72 Chacaíto II",
              description: "SUCURSAL 72 Chacaíto II",
              address: "Boulevard de Sábana Grande con cruce a la Calle Baldo",
              short_name: "Chacaíto II",
              status: true,
              afiliation_number: "238"
            },
            {
              _id: { $oid: "64d5379038242fbb1ee14d53" },
              code: "1001",
              name: "SUCURSAL 01 Centro",
              description: "SUCURSAL 01 Centro",
              address: "Centro Comercial Plaza",
              short_name: "Centro",
              status: true,
              afiliation_number: "101"
            }
          ];
          this.filteredStores = [...this.stores];
          
          this.notification.crediAlert('No se pudieron cargar las tiendas del servidor. Usando datos de prueba.', 'warning');
        }
      });
  }

  filterStores(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredStores = [...this.stores];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredStores = this.stores.filter(store => 
      store.description.toLowerCase().includes(term) ||
      store.code.toLowerCase().includes(term) ||
      store.short_name.toLowerCase().includes(term)
    );
  }

  onStoreSelect(store: Store): void {
    this.loginForm.patchValue({
      selectedStore: store.code,
      storeSearch: store.description
    });
    // Limpiar la lista de sugerencias
    this.filteredStores = [];
  }

  clearStoreSelection(): void {
    this.loginForm.patchValue({
      selectedStore: '',
      storeSearch: ''
    });
    // Restaurar la lista completa de tiendas para nueva búsqueda
    this.filteredStores = [...this.stores];
  }

  onStoreSearchChange(event: any): void {
    const searchTerm = event.target.value;
    
    // Si hay una tienda seleccionada y el usuario está modificando el texto,
    // limpiar la selección para permitir nueva búsqueda
    if (this.loginForm.get('selectedStore')?.value) {
      this.loginForm.patchValue({ selectedStore: '' });
    }
    
    // Filtrar tiendas normalmente
    this.filterStores(searchTerm);
  }

  closeLoginModal(): void {
    this.showLoginModal = false;
    this.loginForm.reset();
    this.filteredStores = [...this.stores]; // Resetear sugerencias
  }

  authenticateCashier(): void {
    if (!this.loginForm.valid) {
      this.notification.crediAlert('Por favor, complete todos los campos requeridos', 'warning');
      return;
    }

    this.isAuthenticating = true;
    const formData = this.loginForm.value;
    const selectedStore = this.stores.find(s => s.code === formData.selectedStore);

    // Llamada real al API de login
    const loginData = {
      username: "admin",
      password: "123456",
      shop: formData.selectedStore,
      cash_register: parseInt(formData.cashRegister),
      userIdentity: formData.cashierDocument
    };

    console.log('Iniciando login con:', loginData);

    const loginHeaders = {
      'Content-Type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'
    };

    this.http.post<any>(`${this.simulatorConfig.cashRegisterUrl}/api/auth/login`, loginData, { headers: loginHeaders })
      .subscribe({
        next: (response) => {
          console.log('=== RESPUESTA COMPLETA DEL LOGIN ===');
          console.log('Respuesta del login:', response);
          console.log('Token recibido:', response.token);
          console.log('Tipo del token:', typeof response.token);
          console.log('Longitud del token:', response.token?.length);
          console.log('=====================================');
          
          // Procesar respuesta exitosa
          this.cashierSession = {
            shop: formData.selectedStore,
            cashRegister: formData.cashRegister,
            cashierDocument: formData.cashierDocument,
            token: response.token,
            storeName: selectedStore?.description || 'Tienda Desconocida'
          };
          
          console.log('=== SESIÓN CREADA ===');
          console.log('cashierSession:', this.cashierSession);
          console.log('Token guardado:', this.cashierSession.token);
          console.log('=====================');

          this.isAuthenticating = false;
          this.showLoginModal = false;
          this.currentView = 'client-search';
          this.isActive = true;
          
          console.log('Login exitoso:', this.cashierSession);
          this.notification.crediAlert(`Login exitoso en ${this.cashierSession.storeName}`, 'success');
        },
        error: (error) => {
          console.log('=== ERROR EN LOGIN ===');
          console.error('Error completo:', error);
          console.error('Status:', error.status);
          console.error('Error response:', error.error);
          console.error('Error message:', error.message);
          console.log('======================');
          
          this.isAuthenticating = false;
          
          let errorMessage = 'Error al iniciar sesión';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.status === 401) {
            errorMessage = 'Credenciales inválidas';
          } else if (error.status === 404) {
            errorMessage = 'Servicio de login no disponible';
          }
          
          this.notification.crediAlert(`Error: ${errorMessage}`, 'error');
        }
      });
  }

  // === GESTIÓN DE CLIENTES ===

  async searchClient(): Promise<void> {
    // VALIDACIÓN DE SEGURIDAD: No permitir búsquedas sin sesión activa
    if (!this.isActive || !this.cashierSession) {
      this.notification.crediAlert('Debe iniciar sesión como cajero para consultar clientes', 'warning');
      this.currentView = 'login';
      return;
    }

    if (!this.clientForm.valid) {
      this.notification.crediAlert('Por favor, ingrese una cédula válida', 'warning');
      return;
    }

    const identity = this.clientForm.value.identity;
    
    try {
      console.log('Buscando cliente por cédula:', identity);
      
      // Llamada real al API de Kredit para buscar por cédula
      const searchData = {
        type_identity: 'CI',
        code_identity: 'V',
        identity: identity
      };

      const headers = {
        'Content-Type': 'application/json',
        'X-Skip-Auth-Interceptor': 'true'
      };

      const response = await this.http.post<any>(
        `${environment.BASE_LA}/api/person/identity`, 
        searchData, 
        { headers }
      ).toPromise();

      console.log('=== RESPUESTA BÚSQUEDA POR CÉDULA ===');
      console.log('Respuesta completa:', response);
      console.log('response.resp:', response.resp);
      console.log('response.resp.person:', response.resp?.person);
      console.log('response.resp.person.nombre:', response.resp?.person?.nombre);
      console.log('response.resp.person.telefono:', response.resp?.person?.telefono);
      console.log('response.resp.person.email:', response.resp?.person?.email);
      console.log('=====================================');

      if (response && response.resp && response.resp.person) {
        // Cliente encontrado - guardar información completa
        this.clientData = { 
          identity,
          fullContactInfo: response.resp
        };

        // Obtener información completa con selfie usando getPersonbyCode
        const personCode = response.resp.person.code;
        if (personCode) {
          try {
            console.log('Obteniendo información completa para código:', personCode);
            const completeData = await this.getCompletePersonData(personCode);
            if (completeData) {
              this.clientData.personCompleteData = completeData;
              console.log('=== INFORMACIÓN COMPLETA OBTENIDA ===');
              console.log('personCompleteData:', this.clientData.personCompleteData);
              console.log('file_selfie:', this.clientData.personCompleteData.file_selfie);
              console.log('================================');
            }
          } catch (error) {
            console.error('Error obteniendo información completa:', error);
          }
        }

        this.currentView = 'options';
        console.log('=== CLIENTE GUARDADO ===');
        console.log('clientData:', this.clientData);
        console.log('fullContactInfo:', this.clientData.fullContactInfo);
        console.log('person:', this.clientData.fullContactInfo?.person);
        console.log('========================');
      } else {
        // Cliente no encontrado
        this.notification.crediAlert('Usuario no registrado en el sistema', 'info');
        this.clientForm.reset();
      }
    } catch (error) {
      console.error('Error buscando cliente:', error);
      this.notification.crediAlert('Error al buscar cliente. Usuario no registrado en el sistema.', 'error');
      this.clientForm.reset();
    }
  }

  // === OPCIONES DE CONSULTA ===

  showClientOptions(): void {
    this.currentView = 'options';
  }

  consultDebts(): void {
    // VALIDACIÓN DE SEGURIDAD: Verificar sesión activa PRIMERO
    if (!this.isActive || !this.cashierSession || !this.cashierSession.token) {
      this.notification.crediAlert('Debe iniciar sesión como cajero para consultar deudas', 'warning');
      this.currentView = 'login';
      this.clientData = null; // Limpiar datos del cliente
      return;
    }

    if (!this.clientData?.identity) {
      this.notification.crediAlert('No hay cliente seleccionado', 'warning');
      return;
    }

    // 💾 SISTEMA DE MEMORIA: Verificar si ya hay una consulta activa para este cliente
    if (this.hasActiveQuery && 
        this.cachedClientIdentity === this.clientData.identity && 
        this.cachedQueryData) {
      console.log('📋 Usando consulta en memoria para cliente:', this.cachedClientIdentity);
      
      // Restaurar datos desde la memoria
      this.currentDebts = this.cachedQueryData.person;
      this.currentTransactionNumber = this.cachedQueryData.transactionNumber;
      this.currentView = 'debts';
      
      // Cargar transacciones asociadas
      this.loadAllTransactions();
      return;
    }

    console.log('Consultando deudas para:', this.clientData.identity);
    console.log('Token enviado:', this.cashierSession.token);
    
    // Llamada real al API de deudas
    const headers = {
      'Authorization': this.cashierSession.token,  // Sin "Bearer" - API lo espera directo
      'Content-Type': 'application/json',
      'X-Skip-Auth-Interceptor': 'true'  // Header para evitar logout automático
    };

    const url = `${this.simulatorConfig.cashRegisterUrl}/api/payments/invoices_information`;
    const params = {
      identity: this.clientData.identity,
      typeIdentity: 'CI',
      codeIdentity: 'V'
    };

    console.log('Headers enviados:', headers);
    console.log('URL completa:', url);
    console.log('Parámetros:', params);

    this.http.get<any>(url, { 
      headers,
      params 
    }).subscribe({
      next: (response) => {
        console.log('Respuesta de deudas:', response);
        
        if (response.res) {
          // Guardar el transactionNumber en memoria
          this.currentTransactionNumber = response.res.transactionNumber;
          
          // Procesar los datos de deudas
          this.currentDebts = response.res.person;
          
          // 🔧 ORDENAMIENTO DE CUOTAS: Ordenar cuotas de menor a mayor número
          if (response.res && response.res.invoices) {
            response.res.invoices.forEach((invoice: Invoice) => {
              if (invoice.cuotas) {
                invoice.cuotas.sort((a: Installment, b: Installment) => a.num_cuota - b.num_cuota);
              }
            });
          }
          
          // 💾 GUARDAR EN MEMORIA: Activar sistema de caché
          this.hasActiveQuery = true;
          this.cachedClientIdentity = this.clientData?.identity || null;
          this.cachedQueryData = {
            person: this.currentDebts,
            transactionNumber: this.currentTransactionNumber,
            timestamp: new Date().getTime()
          };
          console.log('💾 Consulta guardada en memoria para cliente:', this.cachedClientIdentity);
          
          this.currentView = 'debts';
          console.log('Transaction Number guardado:', this.currentTransactionNumber);
        } else {
          this.notification.crediAlert('No se encontraron datos de deudas', 'info');
        }
      },
      error: (error) => {
        console.error('Error consultando deudas:', error);
        console.error('Status del error:', error.status);
        console.error('Respuesta completa del error:', error.error);
        
        let errorMessage = 'Error al consultar deudas';
        let shouldShowError = true;
        
        // Manejo específico para respuestas del API de Kredit
        if (error.error && typeof error.error === 'object') {
          // Verificar si es el error específico de "sin créditos pendientes"
          if (error.error.code === 10 || error.error.msg === 'Usuario sin créditos pendientes') {
            // Este usuario existe pero no tiene deudas pendientes
            console.log('Usuario sin créditos pendientes detectado');
            this.currentDebts = null;
            this.currentView = 'debts';
            this.notification.crediAlert('El cliente no tiene créditos pendientes en este momento', 'info');
            shouldShowError = false;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.msg) {
            errorMessage = error.error.msg;
          }
        }
        
        // Manejo de errores HTTP estándar
        if (shouldShowError) {
          if (error.status === 401) {
            errorMessage = 'Token inválido o expirado. Debe iniciar sesión nuevamente.';
            // Solo cerrar la sesión del SIMULADOR, no de toda la app
            this.logout();
          } else if (error.status === 404) {
            errorMessage = 'Cliente no encontrado';
          } else if (error.status === 500) {
            errorMessage = 'Error del servidor';
          } else if (!errorMessage || errorMessage === 'Error al consultar deudas') {
            errorMessage = `Error ${error.status}: ${error.statusText || 'Error desconocido'}`;
          }
          
          this.notification.crediAlert(`Error: ${errorMessage}`, 'error');
          
          // Volver a la vista de opciones en lugar de cerrar todo
          if (error.status !== 401) {
            this.currentView = 'options';
          }
        }
      }
    });
  }

  consultTransactions(): void {
    // VALIDACIÓN DE SEGURIDAD: Verificar sesión activa PRIMERO
    if (!this.isActive || !this.cashierSession || !this.cashierSession.token) {
      this.notification.crediAlert('Debe iniciar sesión como cajero para consultar transacciones', 'warning');
      this.currentView = 'login';
      this.clientData = null; // Limpiar datos del cliente
      return;
    }

    if (!this.clientData?.identity) {
      this.notification.crediAlert('No hay cliente seleccionado', 'warning');
      return;
    }

    // Cambiar a la vista de transacciones y cargar los datos
    console.log('Consultando transacciones para:', this.clientData.identity);
    this.currentView = 'transactions';
    this.loadAllTransactions();
  }

  // === NAVEGACIÓN ===

  goBack(): void {
    switch (this.currentView) {
      case 'options':
        this.currentView = 'client-search';
        break;
      case 'debts':
      case 'transactions':
        this.currentView = 'options';
        break;
      default:
        this.currentView = 'client-search';
    }
  }

  logout(): void {
    console.log('Cerrando sesión...');
    console.log('cashierSession antes:', this.cashierSession);
    
    // Limpiar completamente la sesión y TODOS los datos sensibles
    this.cashierSession = null;
    this.clientData = null;
    this.currentDebts = null;
    this.currentTransactions = [];
    this.currentTransactionNumber = null;
    this.currentView = 'login'; // IMPORTANTE: volver a vista de login
    this.isActive = false;
    
    // 🧹 LIMPIAR MEMORIA Y SELECCIONES
    this.clearQueryMemory();
    this.selectedTransactions.clear();
    this.isSelectionMode = false;
    
    // También limpiar formularios
    this.loginForm.reset();
    this.clientForm.reset();
    this.filteredStores = [...this.stores];
    
    // Limpiar cualquier token guardado en memoria
    this.simulatorConfig.isConnected = false;
    
    console.log('cashierSession después:', this.cashierSession);
    console.log('currentView después:', this.currentView);
    console.log('isActive después:', this.isActive);
    
    this.notification.crediAlert('Sesión cerrada exitosamente', 'success');
  }

  // === UTILIDADES ===

  getStoreAffiliationNumber(): string {
    if (!this.cashierSession?.shop) return 'N/A';
    
    const store = this.stores.find(s => s.code === this.cashierSession?.shop);
    return store?.afiliation_number || 'N/A';
  }

  getTotalOverdueInstallments(): number {
    if (!this.currentDebts?.invoices) return 0;
    
    let count = 0;
    this.currentDebts.invoices.forEach(invoice => {
      if (invoice.cuotas) {
        count += invoice.cuotas.filter(cuota => cuota.cuota_vencida).length;
      }
    });
    return count;
  }

  getTotalInstallmentsCount(): number {
    if (!this.currentDebts?.invoices) return 0;
    
    let count = 0;
    this.currentDebts.invoices.forEach(invoice => {
      if (invoice.cuotas) {
        count += invoice.cuotas.length;
      }
    });
    return count;
  }

  sanitize(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // === OBTENER INFORMACIÓN COMPLETA DE LA PERSONA ===

  async getCompletePersonData(personCode: string): Promise<PersonCompleteData | null> {
    try {
      console.log('Obteniendo información completa para persona con código:', personCode);
      
      const resp = await this.userService.getPersonbyCode({ code: personCode });
      
      if (resp?.resp?.person) {
        const person = resp.resp.person;
        console.log('Información completa obtenida:', person);
        
        return {
          code: person.code,
          name: person.name,
          lastName: person.lastName,
          second_name: person.second_name,
          second_lastName: person.second_lastName,
          identity: person.identity,
          telefono: person.telefono,
          email: person.email,
          file_selfie: person.file_selfie,
          gender: person.gender
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error obteniendo información completa de la persona:', error);
      return null;
    }
  }

  // === PAYMENT DETAIL MODAL METHODS ===
  
  async openPaymentDetailModal(transaction: any): Promise<void> {
    console.log('🚀 openPaymentDetailModal llamado con transaction:', transaction);
    
    if (!transaction.payments || transaction.payments.length === 0) {
      console.log('❌ No hay pagos en la transacción');
      this.notification.crediAlert('Esta transacción no tiene pagos registrados', 'warning');
      return;
    }

    const paymentCode = transaction.payments[0].payment_code;
    console.log('💳 Payment code encontrado:', paymentCode);
    
    if (!paymentCode) {
      console.log('❌ Payment code es undefined/null');
      this.notification.crediAlert('No se encontró el código del pago', 'warning');
      return;
    }

    // Obtener el code_person desde clientData
    const codePerson = this.clientData?.fullContactInfo?.person?.code;
    console.log('👤 Code person:', codePerson);
    console.log('📦 Client data completo:', this.clientData);
    
    if (!codePerson) {
      console.log('❌ No se encontró code_person en clientData');
      this.notification.crediAlert('No se encontró la información del cliente', 'warning');
      return;
    }

    // Abrir modal y mostrar loading
    this.isLoadingPaymentDetail = true;
    this.isPaymentDetailModalOpen = true;
    this.selectedPaymentDetail = null;
    this.cdr.detectChanges();
    console.log('🎭 Modal abierto - isPaymentDetailModalOpen:', this.isPaymentDetailModalOpen);

    try {
      console.log('🔍 Consultando pago con código:', paymentCode, 'para persona:', codePerson);
      const response = await this.userService.getPaymentByCode(paymentCode, codePerson);
      console.log('📡 Respuesta completa del API:', response);
      
      // Soportar múltiples estructuras de respuesta:
      // - response.resp (frontend normal)
      // - response.res (cash register con wrapper)
      // - response directamente como array (cash register sin wrapper)
      let paymentData: any;
      if (Array.isArray(response)) {
        paymentData = response;
      } else {
        paymentData = response?.resp || response?.res;
      }
      console.log('📊 Payment data extraído:', paymentData);
      
      if (paymentData?.length > 0) {
        this.selectedPaymentDetail = paymentData[0];
        this.isLoadingPaymentDetail = false;
        this.cdr.detectChanges();
        console.log('✅ Detalle del pago obtenido:', this.selectedPaymentDetail);
        console.log('🎭 Estado final - isLoadingPaymentDetail:', this.isLoadingPaymentDetail);
        console.log('🎭 Estado final - isPaymentDetailModalOpen:', this.isPaymentDetailModalOpen);
      } else {
        console.log('❌ paymentData está vacío o no tiene elementos');
        this.notification.crediAlert('No se encontró información del pago', 'error');
        this.closePaymentDetailModal();
      }
    } catch (error) {
      console.error('❌ Error al obtener detalle del pago:', error);
      this.notification.crediAlert('Error al consultar el detalle del pago', 'error');
      this.closePaymentDetailModal();
    } finally {
      this.isLoadingPaymentDetail = false;
      this.cdr.detectChanges();
      console.log('🏁 Finally - isLoadingPaymentDetail:', this.isLoadingPaymentDetail);
      console.log('🏁 Finally - isPaymentDetailModalOpen:', this.isPaymentDetailModalOpen);
    }
  }

  closePaymentDetailModal(): void {
    console.log('🔴 Cerrando modal de detalle de pago');
    this.isPaymentDetailModalOpen = false;
    this.selectedPaymentDetail = null;
    this.isLoadingPaymentDetail = false;
    this.cdr.detectChanges();
  }

  findAutopago(source: string): string {
    const autopago = [
      {key: "BDV", name: "AUTO: BANCO DE VENEZUELA"},
      {key: "BA", name: "AUTO: BANCAAMIGA"},
      {key: "MERCHANT", name: "AUTO: MEGASOFT"},
      {key: "MC", name: "AUTO: MERCANTIL"},
      {key: "AUTO-DETECT", name: "Auto detectado"}
    ];
    
    const found = autopago.find(a => a.key === source);
    return found ? found.name : source;
  }

  redirectIMG(url: string): void {
    window.open(url, '_blank');
  }

  async getBanks(): Promise<void> {
    try {
      const resp: any = await this.userService.getBanks({});
      const banks_resp = resp?.res;
      const banks: any = {};

      banks_resp?.map((value: any) => {
        banks[value.bankCode] = value?.name;
      });

      this.banks = banks;
    } catch (error) {
      console.error('Error al cargar bancos:', error);
    }
  }

  getBankName(bankCode: string): string | null {
    return this.banks[bankCode] || null;
  }

  ngOnDestroy(): void {
    // Asegurar que se restaure el scroll del body al destruir el componente
    document.body.style.overflow = 'auto';
    
    // Cerrar modales si están abiertos
    this.showPaymentModal = false;
    this.isDrawerOpen = false;
    this.isPaymentDetailModalOpen = false;
  }

}